import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { CustomDatePicker } from '../../ui/custom-date-picker';
import { FieldError } from '../StatusBadge';
import { Lock } from 'lucide-react';

export function PaymentContent({ formData, handleFieldChange, errors, compact = false, locked = false }) {
  const inputH = compact ? 'h-12 rounded-xl text-base' : 'h-11 rounded-xl';
  const lockedStyle = 'bg-slate-100 cursor-not-allowed opacity-70';
  
  const getDaysRemaining = () => {
    if (!formData.expected_payment_date || formData.bill_status === 'paid') return '-';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const expectedDate = new Date(formData.expected_payment_date); expectedDate.setHours(0, 0, 0, 0);
    const daysDiff = Math.floor((expectedDate - today) / (1000 * 60 * 60 * 24));
    return daysDiff.toString();
  };
  
  const getDaysRemainingColor = () => {
    if (!formData.expected_payment_date || formData.bill_status === 'paid') return 'bg-slate-50';
    const today = new Date();
    const expectedDate = new Date(formData.expected_payment_date);
    const daysDiff = Math.floor((expectedDate - today) / (1000 * 60 * 60 * 24));
    if (daysDiff < 0) return 'bg-red-100 text-red-700 font-bold';
    if (daysDiff <= 15) return 'bg-yellow-100 text-yellow-700 font-bold';
    return 'bg-green-100 text-green-700 font-bold';
  };
  
  const getDaysLabel = () => {
    if (!formData.expected_payment_date || formData.bill_status === 'paid') return 'Auto-calculated';
    const today = new Date();
    const expectedDate = new Date(formData.expected_payment_date);
    const daysDiff = Math.floor((expectedDate - today) / (1000 * 60 * 60 * 24));
    if (daysDiff < 0) return `${Math.abs(daysDiff)} days overdue`;
    if (daysDiff === 0) return 'Due today';
    return `${daysDiff} days until due`;
  };
  
  const isPastDue = () => {
    if (!formData.expected_payment_date || formData.bill_status === 'paid') return 'No';
    const today = new Date();
    const expectedDate = new Date(formData.expected_payment_date);
    return today > expectedDate ? 'Yes' : 'No';
  };
  
  const getPastDueColor = () => {
    if (!formData.expected_payment_date || formData.bill_status === 'paid') return 'bg-slate-50';
    const today = new Date();
    const expectedDate = new Date(formData.expected_payment_date);
    return today > expectedDate ? 'bg-red-100 text-red-700 font-bold' : 'bg-green-100 text-green-700 font-bold';
  };

  if (locked) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-2 gap-4'}`}>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Expected Payment Date</Label>
            <Input value={formData.expected_payment_date || ''} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Days Remaining</Label>
            <Input value={getDaysRemaining()} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
        </div>
        <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-2 gap-4'}`}>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Actual Payment Date</Label>
            <Input value={formData.actual_payment_date || ''} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Past Due</Label>
            <Input value={isPastDue()} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-2 gap-4'}`}>
        <div className={compact ? 'space-y-1' : 'space-y-2'}>
          {compact && <span className="text-xs text-slate-500">Expected Date</span>}
          {!compact && <Label className="text-sm font-semibold">Expected Payment Date</Label>}
          <Input value={formData.expected_payment_date || 'Auto-calculated'} readOnly className={`${inputH} bg-slate-50`} />
          <p className={`${compact ? 'text-[10px]' : 'text-[10px]'} text-purple-600`}>Date Received + {(parseInt(formData.initial_due_days) || 45) + 15} days</p>
        </div>
        <div className={compact ? 'space-y-1' : 'space-y-2'}>
          {compact && <span className="text-xs text-slate-500">Days Remaining</span>}
          {!compact && <Label className="text-sm font-semibold">Days Remaining</Label>}
          <Input value={getDaysRemaining()} readOnly className={`${inputH} ${getDaysRemainingColor()}`} />
          <p className={`${compact ? 'text-[10px]' : 'text-[10px]'} text-slate-500`}>{getDaysLabel()}</p>
        </div>
      </div>
      <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-2 gap-4'}`}>
        <div className={compact ? 'space-y-1' : 'space-y-2'}>
          {compact && <span className="text-xs text-slate-500">Payment Date</span>}
          {!compact && <Label className="text-sm font-semibold">Payment Date</Label>}
          <Input 
            value={formData.bill_payment_date || formData.actual_payment_date || 'Not set'} 
            readOnly 
            className={`${inputH} ${(formData.bill_payment_date || formData.actual_payment_date) ? 'bg-green-100 text-green-700 font-semibold' : 'bg-slate-50 text-slate-400'}`}
          />
          {!(formData.bill_payment_date || formData.actual_payment_date) && (
            <p className="text-[10px] text-slate-400">Set Payment Date in Billing section</p>
          )}
          {(formData.bill_payment_date || formData.actual_payment_date) && (
            <p className="text-[10px] text-green-600">From Billing section</p>
          )}
        </div>
        <div className={compact ? 'space-y-1' : 'space-y-2'}>
          {compact && <span className="text-xs text-slate-500">Past Due?</span>}
          {!compact && <Label className="text-sm font-semibold">Past Due</Label>}
          <Input value={isPastDue()} readOnly className={`${inputH} ${getPastDueColor()}`} />
          <p className={`${compact ? 'text-[10px]' : 'text-[10px]'} text-slate-500`}>Auto-calculated</p>
        </div>
      </div>
    </div>
  );
}
